import { useEffect, useMemo } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  useGetClientConfigurationBusinessClassificationsQuery,
  useGetClientConfigurationBusinessClassificationValuesQuery,
} from "../store";
import {
  BILLING_DATA_CLASSIFICATION_NO_DATA_MESSAGE,
  isBillingClassificationRowComplete,
  validateBillingClassificationRows,
} from "../utils/invProfileBillingClassification";

function billingClassificationLookupHasNoData({ loading = false, isError = false, count = 0 }) {
  return !loading && !isError && count === 0;
}

function BillingDataClassificationNoDataAlert() {
  return (
    <p className="mt-1 text-sm font-bold text-red-600 dark:text-red-400" role="alert">
      {BILLING_DATA_CLASSIFICATION_NO_DATA_MESSAGE}
    </p>
  );
}

function clearedClassifyFields() {
  return {
    billingDataClassificationValue: "",
    productClass: "",
    productCode: "",
    productType: "",
    productLine: "",
    warehouseName: "",
  };
}

/**
 * One billing classification row (company #, classification LOV, classify value).
 * Mirrors tier row layout on the contracts Add Service panel.
 */
export function BillingClassificationRowEditor({
  row,
  rowIndex,
  rowCount,
  rowErrors,
  assignedCompanyNumbers,
  custIdForQuery,
  fieldClassName,
  onRowPatch,
  onAddRow,
  onRemoveLastRow,
  onLookupStateChange,
  allRows,
  productDivisionsByRowId = {},
  lookupByRowId = {},
}) {
  const companyNumber = String(row.companyNumber ?? "").trim();
  const classification = String(row.billingDataClassification ?? "").trim();
  const billingLookupQueryArg = useMemo(() => {
    if (custIdForQuery == null || !companyNumber) {
      return null;
    }
    return { custId: custIdForQuery, companyNumber };
  }, [custIdForQuery, companyNumber]);
  const billingClassValueQueryArg = useMemo(() => {
    if (billingLookupQueryArg == null || !classification) {
      return null;
    }
    return { ...billingLookupQueryArg, billingClassName: classification };
  }, [billingLookupQueryArg, classification]);

  const {
    data: businessClassificationsData,
    isLoading: isBusinessClassificationsLoading,
    isError: isBusinessClassificationsError,
  } = useGetClientConfigurationBusinessClassificationsQuery(billingLookupQueryArg, {
    skip: billingLookupQueryArg == null,
  });
  const billingDataClassificationOptions = useMemo(() => {
    const fromApi = businessClassificationsData?.billingClassNames ?? [];
    const selected = String(row.billingDataClassification ?? "").trim();
    if (selected && !fromApi.includes(selected)) {
      return [selected, ...fromApi];
    }
    return fromApi;
  }, [businessClassificationsData?.billingClassNames, row.billingDataClassification]);
  const showBusinessClassificationNoData =
    billingLookupQueryArg != null &&
    !isBusinessClassificationsLoading &&
    !isBusinessClassificationsError &&
    (businessClassificationsData?.billingClassNames ?? []).length === 0;

  const {
    data: businessClassificationValuesData,
    isLoading: isBusinessClassificationValuesLoading,
    isError: isBusinessClassificationValuesError,
  } = useGetClientConfigurationBusinessClassificationValuesQuery(billingClassValueQueryArg, {
    skip: billingClassValueQueryArg == null,
  });
  const billingReportClassifyOptions = useMemo(() => {
    const fromApi = businessClassificationValuesData?.billingClassValues ?? [];
    const selected = String(row.billingDataClassificationValue ?? "").trim();
    if (selected && !fromApi.includes(selected)) {
      return [selected, ...fromApi];
    }
    return fromApi;
  }, [businessClassificationValuesData?.billingClassValues, row.billingDataClassificationValue]);
  const showClassifyField = Boolean(classification);
  const showBillingReportClassifyNoData = billingClassificationLookupHasNoData({
    loading: Boolean(showClassifyField && isBusinessClassificationValuesLoading),
    isError: Boolean(showClassifyField && isBusinessClassificationValuesError),
    count: showClassifyField ? (businessClassificationValuesData?.billingClassValues ?? []).length : 0,
  });

  const lookupState = useMemo(
    () => ({
      billingReportClassify: {
        loading: Boolean(showClassifyField && isBusinessClassificationValuesLoading),
        isError: Boolean(showClassifyField && isBusinessClassificationValuesError),
        optionsLength: showClassifyField
          ? (businessClassificationValuesData?.billingClassValues ?? []).length
          : 0,
      },
    }),
    [
      showClassifyField,
      isBusinessClassificationValuesLoading,
      isBusinessClassificationValuesError,
      businessClassificationValuesData?.billingClassValues,
    ]
  );

  useEffect(() => {
    onLookupStateChange?.(row.id, lookupState);
  }, [row.id, lookupState, onLookupStateChange]);

  const isLastRow = rowIndex === rowCount - 1;
  const rowIsComplete = isBillingClassificationRowComplete(
    row,
    lookupState,
    assignedCompanyNumbers
  );
  const lookupByRowIdForValidation = {
    ...lookupByRowId,
    [row.id]: lookupState,
  };
  const rowErrorsList = validateBillingClassificationRows(
    allRows ?? [row],
    assignedCompanyNumbers,
    lookupByRowIdForValidation,
    productDivisionsByRowId
  );
  const rowHasValidationErrors = Boolean(rowErrorsList?.[rowIndex]);
  const canAddAnotherRow = isLastRow && rowIsComplete && !rowHasValidationErrors;

  const singleCompany = assignedCompanyNumbers.length === 1;
  const companyDisplay = singleCompany ? assignedCompanyNumbers[0] : companyNumber;

  return (
    <div
      className={
        rowCount > 1
          ? "rounded-md border border-gray-200 dark:border-gray-700 p-3"
          : undefined
      }
    >
      {rowCount > 1 ? (
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">
          Classification {rowIndex + 1}
        </p>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Company Number *
          </label>
          {assignedCompanyNumbers.length === 0 ? (
            <input
              type="text"
              readOnly
              value=""
              placeholder="No company number assigned"
              aria-readonly="true"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
            />
          ) : singleCompany ? (
            <input
              type="text"
              readOnly
              value={companyDisplay}
              aria-readonly="true"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
            />
          ) : (
            <select
              value={companyNumber}
              onChange={(e) =>
                onRowPatch({
                  companyNumber: e.target.value,
                  billingDataClassification: "",
                  ...clearedClassifyFields(),
                })
              }
              aria-invalid={Boolean(rowErrors?.companyNumber)}
              className={fieldClassName(Boolean(rowErrors?.companyNumber))}
            >
              <option value="">Select company number</option>
              {assignedCompanyNumbers.map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
          )}
          {rowErrors?.companyNumber ? (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              {rowErrors.companyNumber}
            </p>
          ) : null}
          {isLastRow ? (
            <button
              type="button"
              disabled={!canAddAnotherRow}
              onClick={() => {
                if (!canAddAnotherRow) {
                  return;
                }
                onAddRow();
              }}
              className={`app-btn-gloss mt-2 px-2 py-1 text-xs ${
                !canAddAnotherRow ? "cursor-not-allowed opacity-50" : ""
              }`}
              title={
                !canAddAnotherRow
                  ? "Complete this classification before adding another"
                  : "Add another classification"
              }
              aria-label={`Add classification after classification ${rowIndex + 1}`}
              aria-disabled={!canAddAnotherRow}
            >
              <Plus className="w-3.5 h-3.5" />
              Add classification
            </button>
          ) : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Billing Data Classification *
          </label>
          <select
            value={row.billingDataClassification ?? ""}
            onChange={(e) =>
              onRowPatch({
                billingDataClassification: e.target.value,
                ...clearedClassifyFields(),
              })
            }
            disabled={
              !companyNumber ||
              isBusinessClassificationsLoading ||
              isBusinessClassificationsError
            }
            aria-invalid={Boolean(
              rowErrors?.billingDataClassification ||
                isBusinessClassificationsError ||
                showBusinessClassificationNoData
            )}
            className={fieldClassName(
              Boolean(
                rowErrors?.billingDataClassification ||
                  isBusinessClassificationsError ||
                  showBusinessClassificationNoData
              )
            )}
          >
            <option value="">
              {!companyNumber
                ? "Select a company number first"
                : isBusinessClassificationsLoading
                  ? "Loading billing data classifications…"
                  : "Select billing data classification"}
            </option>
            {billingDataClassificationOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {isBusinessClassificationsError ? (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              Unable to load billing data classifications.
            </p>
          ) : showBusinessClassificationNoData ? (
            <BillingDataClassificationNoDataAlert />
          ) : rowErrors?.billingDataClassification ? (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              {rowErrors.billingDataClassification}
            </p>
          ) : null}
        </div>

        <div>
          {showClassifyField ? (
            <>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Billing Report Classify *
              </label>
              <select
                value={row.billingDataClassificationValue ?? ""}
                onChange={(e) =>
                  onRowPatch({ billingDataClassificationValue: e.target.value })
                }
                disabled={
                  isBusinessClassificationValuesLoading || isBusinessClassificationValuesError
                }
                aria-invalid={Boolean(
                  rowErrors?.billingDataClassificationValue || showBillingReportClassifyNoData
                )}
                className={fieldClassName(
                  Boolean(
                    rowErrors?.billingDataClassificationValue || showBillingReportClassifyNoData
                  )
                )}
              >
                <option value="">
                  {isBusinessClassificationValuesLoading
                    ? "Loading billing report classifications…"
                    : "Select billing report classify"}
                </option>
                {billingReportClassifyOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {isBusinessClassificationValuesError ? (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                  Unable to load billing report classifications.
                </p>
              ) : showBillingReportClassifyNoData ? (
                <BillingDataClassificationNoDataAlert />
              ) : rowErrors?.billingDataClassificationValue ? (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                  {rowErrors.billingDataClassificationValue}
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic pt-8">
              Select a billing data classification
            </p>
          )}
        </div>
      </div>
      {rowIndex === rowCount - 1 && rowCount > 1 ? (
        <div className="mt-3">
          <button
            type="button"
            onClick={onRemoveLastRow}
            className="inline-flex items-center gap-1 rounded border border-red-300 dark:border-red-800 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
            title="Remove last classification"
            aria-label="Remove last classification"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Remove
          </button>
        </div>
      ) : null}
    </div>
  );
}
